import React, { useState, useEffect, useRef } from 'react';
import { Box, Container } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { useAuth } from '../contexts/AuthContext';
import { streamService } from '../services/streamService';
import { programService } from '../services/programService';
import { birthdayService } from '../services/birthdayService';
import { authService } from '../services/authService';
import { userService } from '../services/userService';

import BannerCarousel from '../components/home/BannerCarousel';
import WelcomeSection from '../components/home/WelcomeSection';
import LiveStageSection from '../components/home/LiveStageSection';
import TodayScheduleSection from '../components/home/TodayScheduleSection';
import TeamBirthdaySection from '../components/home/TeamBirthdaySection';
import EventsSection from '../components/home/EventsSection';
import FloatingActions from '../components/home/FloatingActions';
import EventParticipationModal from '../components/modals/EventParticipationModal';
import GoogleCommentsSection from '../components/home/GoogleCommentsSection';

const Home = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isTheatreMode, setIsTheatreMode] = useState(false);
  const [participationModal, setParticipationModal] = useState({ open: false, event: null });
  const [isBgToggling, setIsBgToggling] = useState(false);
  const [bgLayer1, setBgLayer1] = useState(null);
  const [bgLayer2, setBgLayer2] = useState(null);
  const [activeLayer, setActiveLayer] = useState(1);
  const leftColumnRef = useRef(null);
  const [leftHeight, setLeftHeight] = useState(null);

  // Synchronize height of right column to precisely match left column (LiveStage / Chat base)
  useEffect(() => {
    const el = leftColumnRef.current;
    if (!el) return;

    const updateHeight = () => {
      if (el) {
        const measured = el.getBoundingClientRect().height;
        if (measured > 0) {
          setLeftHeight(Math.round(measured));
        }
      }
    };

    updateHeight();

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateHeight();
      });
      resizeObserver.observe(el);
    }
    window.addEventListener('resize', updateHeight);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  // Fetch current user (only when authenticated to prevent 401 errors for visitors)
  const hasToken = !!localStorage.getItem('token');
  const { data: currentUserData } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => authService.getMe().then((res) => res.data),
    enabled: hasToken,
  });
  const currentUser = currentUserData?.user || user;

  // Fetch live program from backend
  const { data: liveProgramData } = useQuery({
    queryKey: ['liveProgram'],
    queryFn: () => programService.getLiveProgram(),
    refetchInterval: 15000,
  });

  // Fetch today's schedule
  const { data: todayScheduleData, isLoading: scheduleLoading } = useQuery({
    queryKey: ['todaySchedule'],
    queryFn: () => {
      const today = new Date().toLocaleDateString('en-US', { weekday: 'long', timeZone: 'America/Sao_Paulo' }).toLowerCase();
      return programService.getScheduleByDay(today);
    },
    refetchInterval: 30000,
  });

  // Fetch events
  const { data: eventsData } = useQuery({
    queryKey: ['events'],
    queryFn: () => streamService.getEvents({ upcoming: 'true' }),
  });

  // Fetch birthday users
  const { data: birthdayData, isLoading: birthdayLoading } = useQuery({
    queryKey: ['birthdayUsers'],
    queryFn: () => birthdayService.getBirthdayUsers(),
  });

  const liveProgram = liveProgramData?.data;
  const todaySchedule = todayScheduleData?.data;
  const events = Array.isArray(eventsData?.data?.events) ? eventsData.data.events : [];
  const birthdayUsers = Array.isArray(birthdayData?.birthdayUsers) ? birthdayData.birthdayUsers : [];

  // Helper functions for schedule
  const formatTime = (time) => {
    if (!time) return '';
    return time.slice(0, 5);
  };

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString('en-GB', { timeZone: 'America/Sao_Paulo', hour12: false });
  };

  const isProgramLive = (program) => {
    if (!program || !program.start_time || !program.end_time) return false;
    const currentTime = getCurrentTime();
    const startTime = program.start_time;
    const endTime = program.end_time;

    if (startTime > endTime) {
      return currentTime >= startTime || currentTime <= endTime;
    }
    return currentTime >= startTime && currentTime <= endTime;
  };

  // Chat Background transition logic
  useEffect(() => {
    if (liveProgram?.type === 'program' && liveProgram?.data?.dj?.chat_bg_active) {
      const dj = liveProgram.data.dj;
      const bgIndex = dj.chat_bg_index || 1;
      const newBgUrl = dj[`chat_bg_${bgIndex}`];

      if (newBgUrl) {
        const bgStyle = `linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1)), url(${newBgUrl})`;

        if (activeLayer === 1) {
          if (bgLayer1 !== bgStyle) {
            setBgLayer2(bgStyle);
            setActiveLayer(2);
          }
        } else {
          if (bgLayer2 !== bgStyle) {
            setBgLayer1(bgStyle);
            setActiveLayer(1);
          }
        }
      }
    } else {
      if (activeLayer === 1) {
        if (bgLayer1 !== null) {
          setBgLayer2(null);
          setActiveLayer(2);
        }
      } else {
        if (bgLayer2 !== null) {
          setBgLayer1(null);
          setActiveLayer(1);
        }
      }
    }
  }, [liveProgram, activeLayer, bgLayer1, bgLayer2]);

  const handleToggleChatBg = async () => {
    if (liveProgram?.type !== 'program' || !liveProgram?.data?.dj || isBgToggling) return;

    try {
      setIsBgToggling(true);
      const response = await userService.toggleChatActive();
      toast.success(response.data.message);
      queryClient.invalidateQueries(['liveProgram']);
      queryClient.invalidateQueries(['currentUser']);
    } catch (error) {
      console.error('Error toggling chat bg:', error);
      toast.error('Erro ao alternar background');
    } finally {
      setIsBgToggling(false);
    }
  };

  const isCurrentUserLiveDJ =
    liveProgram?.type === 'program' &&
    Number(liveProgram?.data?.dj?.id) === Number(currentUser?.id);

  return (
    <Box sx={{ minHeight: '100vh', pb: 4 }}>
      {/* Hero Section with Banners */}
      <Box sx={{
        mb: { xs: 2, md: 3 },
        pb: { xs: 1, md: 2 },
        '@media (max-height: 750px)': {
          mb: 1.5,
          pb: 0.5,
        },
      }}>
        <Container maxWidth="lg">
          <BannerCarousel />
        </Container>
      </Box>

      {/* Main Layout - Live Stage (Video + Chat), Google Comments & Schedule */}
      <Container
        maxWidth={isVideoOpen ? false : 'xl'}
        sx={{
          pb: 4,
          pl: { xs: 2, lg: 3 },
          pr: { xs: 2, lg: isVideoOpen ? 3 : '75px', xl: isVideoOpen ? 3 : '75px' },
          transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 3,
            width: '100%',
            alignItems: { xs: 'stretch', lg: 'flex-start' },
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* Main Column - Greeting + Live Stage (Video / Chat) */}
          <Box
            ref={leftColumnRef}
            sx={{
              display: { xs: 'contents', lg: 'flex' },
              flexDirection: 'column',
              flex: (isTheatreMode || isVideoOpen) ? '1 1 100%' : '1 1 66.666%',
              width: (isTheatreMode || isVideoOpen)
                ? '100%'
                : { xs: '100%', lg: '66.666%' },
              minWidth: 0,
              transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            {/* 1. Saudação ao visitante: Centralizada acima do Bate-Papo */}
            <Box sx={{
              order: { xs: 1, lg: 'unset' },
              width: '100%',
              mb: { xs: 1.5, lg: 2 },
              '@media (max-height: 750px)': {
                mb: 1,
              },
            }}>
              <WelcomeSection />
            </Box>

            {/* 3. Live Stage (Bate-Papo da rádio / Vídeo) */}
            <Box sx={{ order: { xs: 3, lg: 'unset' }, width: '100%', display: 'flex', flexDirection: 'column' }}>
              <LiveStageSection
                liveProgram={liveProgram}
                activeLayer={activeLayer}
                bgLayer1={bgLayer1}
                bgLayer2={bgLayer2}
                isVideoOpen={isVideoOpen}
                setIsVideoOpen={setIsVideoOpen}
                isTheatreMode={isTheatreMode}
                setIsTheatreMode={setIsTheatreMode}
              />
            </Box>
          </Box>

          {/* Right Column - Today's Schedule + Google Comments (Strictly clamped to left column height) */}
          <Box
            sx={{
              display: (isTheatreMode || isVideoOpen)
                ? 'none'
                : { xs: 'contents', lg: 'flex' },
              flexDirection: 'column',
              flex: '1 1 33.333%',
              width: { xs: '100%', lg: '33.333%' },
              height: { xs: 'auto', lg: leftHeight ? `${leftHeight}px` : 'calc(80vh + 116px)' },
              maxHeight: { xs: 'none', lg: leftHeight ? `${leftHeight}px` : 'calc(80vh + 116px)' },
              minWidth: 0,
              overflow: 'hidden',
              transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            {/* 1. Hoje na Rádio: Agora no topo */}
            <Box
              sx={{
                order: { xs: 2, lg: 'unset' },
                width: '100%',
                flex: 1,
                minHeight: 0,
                mb: { xs: 2, lg: 2 },
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <TodayScheduleSection
                todaySchedule={todaySchedule}
                scheduleLoading={scheduleLoading}
                isProgramLive={isProgramLive}
                formatTime={formatTime}
                liveProgram={liveProgram}
              />
            </Box>

            {/* 2. Mural de Recados: Abaixo de Hoje na Rádio */}
            <Box
              sx={{
                order: { xs: 4, lg: 'unset' },
                width: '100%',
                flexShrink: 0,
              }}
            >
              <GoogleCommentsSection />
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Team & Birthday Section */}
      <Box sx={{ contentVisibility: 'auto', containIntrinsicSize: '0 600px' }}>
        <TeamBirthdaySection
          birthdayUsers={birthdayUsers}
          birthdayLoading={birthdayLoading}
          currentMonth={birthdayData?.currentMonth}
        />
      </Box>

      {/* Events Section */}
      <Box sx={{ contentVisibility: 'auto', containIntrinsicSize: '0 500px' }}>
        <EventsSection
          events={events}
          onParticipate={(event) => setParticipationModal({ open: true, event })}
        />
      </Box>

      {/* Modal de Participação em Eventos */}
      <EventParticipationModal
        open={participationModal.open}
        onClose={() => setParticipationModal({ open: false, event: null })}
        event={participationModal.event}
      />

      {/* Floating Buttons Menu (Desktop & Mobile) */}
      <FloatingActions
        isVideoOpen={isVideoOpen}
        setIsVideoOpen={setIsVideoOpen}
        isCurrentUserLiveDJ={isCurrentUserLiveDJ}
        isBgToggling={isBgToggling}
        handleToggleChatBg={handleToggleChatBg}
        liveProgram={liveProgram}
      />
    </Box>
  );
};

export default Home;
